import loadable from '@loadable/component';
import BpkIconAlert from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/alert--active';
import BpkIconCalendar from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/calendar';
import BpkIconCars from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/cars';
import BpkIconChart from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/chart';
import BpkIconFlight from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/flight';
import BpkIconFlightFlexible from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/flight-flexible';
import BpkIconHotelFlexible from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/hotel-flexible';
import BpkIconHotels from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/hotels';
import BpkIconInsurance from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/insurance';
import BpkIconPriceTag from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/price-tag';
import BpkIconSearch from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/search';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import {
  ACTION_TYPE,
  COMPONENT_NAME,
} from '@skyscanner-internal/landing-pages-shared-constants/constants';
import classNames from 'classnames';

import logMiniEventHelper from '../../mini-event/logMiniEventHelper';
import withErrorBoundary from '../ErrorBoundary';
import { useMBDContext } from '../common/MBD/useMBDContext';

import ConfidenceMessagingV2 from './ConfidenceMessagingV2';

import type { ConfidenceMessagingProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/ConfidenceMessaging';

import STYLES from './ConfidenceMessaging.module.scss';

const IntersectionObserverWrapper = loadable(
  () => import('../IntersectionObserverWrapper/IntersectionObserverWrapper'),
);

type ConfidenceMessagingIcon =
  ConfidenceMessagingProps['dataList'][number]['icon'];

const getIconComponent = (icon: ConfidenceMessagingIcon) => {
  switch (icon) {
    case 'flight':
      return BpkIconFlight;
    case 'cars':
      return BpkIconCars;
    case 'hotels':
      return BpkIconHotels;
    case 'calendar':
      return BpkIconCalendar;
    case 'price-tag':
      return BpkIconPriceTag;
    case 'search':
      return BpkIconSearch;
    case 'hotel-flexible':
      return BpkIconHotelFlexible;
    case 'alert':
      return BpkIconAlert;
    case 'chart':
      return BpkIconChart;
    case 'flight-flexible':
      return BpkIconFlightFlexible;
    case 'insurance':
      return BpkIconInsurance;
    default:
      throw Error(`Icon component not defined for icon name "${icon}"`);
  }
};

const ConfidenceMessaging = (props: ConfidenceMessagingProps) => {
  const { buildMBDParam, rawComponentName } = useMBDContext();

  if (!props?.dataList?.length) {
    return null;
  }

  const useVerticalLayout = !!props.useVerticalLayout;
  const debug = !!props.debug;

  const mbdComponentName = `${rawComponentName}_item`;

  return (
    <div
      data-tracking-element-id={rawComponentName}
      data-tracking-common-params={buildMBDParam(rawComponentName, {
        version: 'v1',
      })}
      className={classNames(STYLES.ConfidenceMessaging, {
        [STYLES.debug]: debug,
        [STYLES.vertical]: useVerticalLayout,
      })}
      data-testid="confidenceMessaging"
    >
      {props.dataList.map((item, index) => {
        const Icon = getIconComponent(item.icon);
        return (
          <div
            data-tracking-element-id={`${mbdComponentName}_${index}`}
            data-tracking-common-params={buildMBDParam(mbdComponentName, {
              version: 'v1',
            })}
            key={item.icon}
            className={STYLES.item}
          >
            <div className={STYLES.icon}>
              <Icon />
            </div>
            <div>
              <BpkText
                className={STYLES.description}
                tagName="p"
                textStyle={TEXT_STYLES.label1}
              >
                {item.description}
              </BpkText>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const ConfidenceMessagingWrapper = (props: ConfidenceMessagingProps) => (
  <IntersectionObserverWrapper
    onElementSeen={() =>
      logMiniEventHelper({
        action_type: ACTION_TYPE.COMPONENT_VISIBLE,
        component_name: COMPONENT_NAME.CONFIDENCE_MESSAGING,
      })
    }
  >
    {props.useV2Component ? (
      <ConfidenceMessagingV2 {...props} />
    ) : (
      <ConfidenceMessaging {...props} />
    )}
  </IntersectionObserverWrapper>
);

const ConfidenceMessagingWithErrorBoundary = withErrorBoundary(
  ConfidenceMessagingWrapper,
  'confidence-messaging-component',
);
export default ConfidenceMessagingWithErrorBoundary;
